import React from "react";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import type { Analysis } from "./Analysis";
import call from "./call";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { Setup } from "./useSetups";
import { computeEndDate, useSetups } from "./useSetups";

const comparator = comparatorBy("date");
const PATH = "/projects/:projectId(uuid)/active-analyses" as const;

function useActiveAnalysesStream(projectId: string) {
  const result = useQuery(PATH, { routeParams: { projectId }, headers: { "json-stream": true } });

  const analyses = result.body;
  const streaming = result.streaming;

  const getCachedAnalyses = useGetQueryData(PATH, {
    routeParams: { projectId },
  });
  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const [{ body: analyses }, { body: deactivatedAnalysisIds }] = await Promise.all([
        call("GET", PATH, {
          routeParams: { projectId },
          query: { since },
          headers: { "json-stream": false },
        }),
        call("GET", "/projects/:projectId(uuid)/deactived-analysis-ids", {
          routeParams: { projectId },
          query: { since: new Date(since) },
        }),
      ]);

      return [analyses, deactivatedAnalysisIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "active_analyses", projectId },
    {
      readItems: React.useCallback(() => getCachedAnalyses()?.body, [getCachedAnalyses]),
      fetchUpdatedItems,
      mutateItems: useMutateBody(PATH, {
        routeParams: { projectId },
      }),
    },
    !streaming,
  );

  return React.useMemo(
    () => ({ isStreaming: streaming, activeAnalyses: analyses.sort(comparator) }),
    [analyses, streaming],
  );
}

export function useActiveAnalysisCount(
  projectId: string,
  fromTo: { from: Date | null; to: Date | null } | null | false,
) {
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  return React.useMemo(() => {
    if (fromTo === null) return activeAnalyses.length;
    if (fromTo === false) return 0;
    const from = fromTo.from?.getTime() ?? null;
    const to = fromTo.to?.getTime() ?? null;
    if (from === null && to === null) return activeAnalyses.length;
    let count = 0;
    for (const { date } of activeAnalyses)
      if ((from === null || date >= from) && (to === null || date <= to)) count++;
    return count;
  }, [activeAnalyses, fromTo]);
}

export function getSetupActiveAnalysisCount(
  setup: Setup,
  setups: Setup[],
  analyses: Analysis[],
): number {
  const startDate = setup.startDate ? new Date(setup.startDate).getTime() : null;
  const endDateStr = computeEndDate(setup, setups);
  const endDate = endDateStr ? new Date(endDateStr).getTime() : null;
  if (startDate === null && endDate === null) return analyses.length;

  let count = 0;
  for (const { date } of analyses)
    if ((startDate === null || date >= startDate) && (endDate === null || date <= endDate)) count++;

  return count;
}

export function useSetupActiveAnalysisCount(projectId: string, setupId: string) {
  const setups = useSetups(projectId);
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  const setup = setups.find(setup => setup.id === setupId);
  return React.useMemo(
    () => (setup ? getSetupActiveAnalysisCount(setup, setups, activeAnalyses) : 0),
    [activeAnalyses, setup, setups],
  );
}

export type ActiveAnalysis = ReturnType<typeof useActiveAnalysesStream>["activeAnalyses"][number];

export function isActiveAnalysis(analysis: Analysis | ActiveAnalysis): analysis is ActiveAnalysis {
  return "status" in analysis;
}

export type ActiveAnalysisQuantityValue = ActiveAnalysis["quantityValues"][number];

export type AnalysisStatus = ActiveAnalysis["status"];

export default useActiveAnalysesStream;
